import { FC, useEffect, useRef, useState } from 'react';
import { TOption } from '@src/types';
import { cn } from '@lib/utils.ts';

interface TypeSelectorProps {
  tabActive: number;
  tabs: TOption[];
  onChangeTab: (index: number, item: TOption) => void;
  className?: string;
  variant?: 'default' | 'gradient';
  disabled?: boolean;
}

export const TypeSelector: FC<TypeSelectorProps> = ({
  tabActive,
  tabs,
  onChangeTab,
  className,
  variant,
  disabled,
}) => {
  const [boxStyle, setBoxStyle] = useState<{ left: number; width: number }>({
    left: 0,
    width: 0,
  });
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      const activeTab = tabRefs.current[tabActive];

      if (activeTab) {
        setBoxStyle({
          left: activeTab.offsetLeft,
          width: activeTab.offsetWidth,
        });
      }
    });

    tabRefs.current.forEach((el) => el && observer.observe(el));

    return () => observer.disconnect();
  }, [tabActive, tabs]);

  useEffect(() => {
    const activeTab = tabRefs.current[tabActive];

    if (activeTab) {
      setBoxStyle({
        left: activeTab.offsetLeft,
        width: activeTab.offsetWidth,
      });
    }
  }, [tabActive]);

  const handleChangeTab = (index: number, item: TOption) => {
    if (item.disabled || disabled) return;
    onChangeTab(index, item);
  };

  return (
    <div className={cn('flex relative gap-x-[35px] pb-[7px]', className)}>
      <div
        className={cn('absolute bottom-0 origin-top bg-white')}
        style={{
          width: boxStyle.width,
          left: boxStyle.left,
          zIndex: 0,
          height: '1px',
          transition: 'all 0.3s',
        }}
      />
      {tabs.map((e, i) => {
        const isActive = tabActive === i;

        return (
          <div
            key={`${e.value}_index${i}`}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            className={cn(
              e.disabled || disabled ? 'cursor-not-allowed' : 'cursor-pointer',
              'z-1 relative flex items-center',
              variant === 'gradient' ? 'px-8' : ''
            )}
            style={{ transition: 'all 0.3s' }}
            onClick={() => handleChangeTab(i, e)}
          >
            <p
              className={'text-sm text-white'}
              style={{
                fontWeight: isActive ? 700 : 300,
              }}
            >
              {e.label}
            </p>
          </div>
        );
      })}
    </div>
  );
};
